import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Twitter,
  Facebook,
  ArrowUpRight,
  Sparkles,
  TrendingUp,
  Shield,
  Cpu,
  Network,
  Layers,
} from 'lucide-react';
import { companyInfo } from '../mock';

const SERVICE_PILLARS = [
  {
    id: 'ai-insights',
    icon: Sparkles,
    title: 'Latest AI Insights',
    items: [
      'GenAI Readiness & Strategy',
      'Responsible AI Frameworks',
      'AI for Finance & Tax',
    ],
  },
  {
    id: 'strategy-transactions',
    icon: TrendingUp,
    title: 'Strategy & Transactions',
    items: [
      'M&A · Buy-side / Sell-side',
      'Capital Strategy & Fundraising',
      'Valuations · DD · PMI',
    ],
  },
  {
    id: 'risk-consulting',
    icon: Shield,
    title: 'Risk Consulting',
    items: [
      'Enterprise Risk · ICFR',
      'Internal Audit Transformation',
      'Regulatory & Forensic Risk',
    ],
  },
  {
    id: 'technology-consulting',
    icon: Cpu,
    title: 'Technology Consulting',
    items: [
      'Digital & Finance Transformation',
      'ERP · Data · Analytics',
      'Cyber, Cloud & RPA',
    ],
  },
  {
    id: 'managed-services',
    icon: Network,
    title: 'Managed Services',
    items: [
      'F&A · Tax · Payroll Ops',
      'Virtual CFO · Compliance',
      'Internal Audit-as-a-Service',
    ],
  },
];

const FOUNDATION_SERVICES = [
  'Statutory & Tax Audit',
  'GST & Indirect Tax',
  'Direct Tax & Transfer Pricing',
  'NRI & Cross-Border (FEMA)',
  'Company Incorporation · ROC',
  'Startup India · ESOP · Fundraising',
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="contact"
      className="relative bg-[#0a0f1e] text-white overflow-hidden"
      data-testid="site-footer"
    >
      {/* Decorative backdrop */}
      <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff22_1px,transparent_1px),linear-gradient(to_bottom,#ffffff22_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
      <div className="absolute -top-40 right-[-10%] w-[600px] h-[600px] bg-brand-blue/15 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute -bottom-40 left-[-5%] w-[500px] h-[500px] bg-brand-yellow/10 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%] pt-20 pb-10">
        {/* ============= TOP BAND: Service Summary ============= */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-4">
                <Layers size={14} className="text-brand-yellow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  Service Summary
                </span>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl font-black text-white leading-tight tracking-tight">
                Five practices.{' '}
                <span className="text-brand-yellow">One integrated team.</span>
              </h3>
            </div>
            <Link
              to="/services"
              onClick={() => window.scrollTo(0, 0)}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-white border border-white/30 hover:border-brand-yellow hover:text-brand-yellow px-5 py-3 transition-all duration-300"
              data-testid="footer-explore-services"
            >
              Explore all services
              <ArrowUpRight
                size={16}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </Link>
          </div>

          {/* 5 Pillars grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {SERVICE_PILLARS.map((p, idx) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.id}
                  to="/services"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group bg-[#0a0f1e] hover:bg-white/[0.04] p-6 transition-all duration-300 relative"
                  data-testid={`footer-pillar-${p.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/5 group-hover:bg-brand-yellow/20 transition-colors">
                      <Icon className="text-brand-yellow" size={18} strokeWidth={1.75} />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-white/30 group-hover:text-brand-yellow transition-colors">
                      0{idx + 1}
                    </span>
                  </div>
                  <h4 className="font-heading text-base font-bold text-white mb-3 leading-tight">
                    {p.title}
                  </h4>
                  <ul className="space-y-1.5">
                    {p.items.map((item, i) => (
                      <li
                        key={i}
                        className="text-[12px] text-white/60 group-hover:text-white/85 leading-snug transition-colors"
                      >
                        · {item}
                      </li>
                    ))}
                  </ul>
                  <ArrowUpRight
                    size={14}
                    className="absolute top-4 right-4 text-brand-yellow opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
                  />
                </Link>
              );
            })}
          </div>

          {/* Foundation services strip */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mr-2">
              Foundation practice ·
            </span>
            {FOUNDATION_SERVICES.map((s, i) => (
              <span
                key={i}
                className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 text-white/75 hover:text-brand-yellow hover:border-brand-yellow/40 transition-colors"
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* ============= MID BAND: Brand + Contact + Quick Links ============= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-12 border-t border-white/10">
          {/* Brand */}
          <div className="lg:col-span-5">
            <img
              src="https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/ckmcku7b_RST%20Logo%20for%20Website.png"
              alt="RST & Co. Chartered Accountants"
              className="h-16 w-auto object-contain mb-5 bg-white p-2"
            />
            <div className="mb-5" lang="kn">
              <p className="font-heading text-xl font-bold text-white leading-tight">
                ಆರ್ ಎಸ್ ಟಿ ಅಂಡ್ ಕಂ
              </p>
              <p className="text-sm text-brand-yellow font-medium tracking-wide">
                ಸನ್ನದು ಲೆಕ್ಕ ಪರಿಶೋಧಕರು
              </p>
            </div>
            <p className="text-sm text-white/65 leading-relaxed max-w-md mb-6">
              Your trusted partner for assurance, taxation, advisory, and modern consulting —
              built for entrepreneurs, CFOs, and boards across India.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow/10 flex items-center justify-center transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin size={16} className="text-white/70" />
              </a>
              <a
                href="#"
                className="w-10 h-10 border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow/10 flex items-center justify-center transition-all"
                aria-label="Twitter"
              >
                <Twitter size={16} className="text-white/70" />
              </a>
              <a
                href="#"
                className="w-10 h-10 border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow/10 flex items-center justify-center transition-all"
                aria-label="Facebook"
              >
                <Facebook size={16} className="text-white/70" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase mb-5">
              Get in touch
            </h4>
            <div className="space-y-4">
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-start gap-3 text-white/80 hover:text-brand-yellow transition-colors break-all"
              >
                <Mail className="text-brand-yellow flex-shrink-0 mt-1" size={16} />
                <span className="text-sm">{companyInfo.email}</span>
              </a>
              {/* Head Office */}
              <div className="pt-3 border-t border-white/10">
                <div className="font-mono text-[10px] tracking-[0.2em] text-brand-yellow uppercase mb-2">
                  Head Office · Nagarabhavi
                </div>
                <a
                  href={companyInfo.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/70 hover:text-brand-yellow transition-colors group"
                  data-testid="footer-address-link"
                >
                  <MapPin className="text-brand-yellow flex-shrink-0 mt-1" size={16} />
                  <span className="text-sm leading-relaxed group-hover:underline">
                    {companyInfo.address}
                  </span>
                </a>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-3 mt-2 text-white/70 hover:text-brand-yellow transition-colors"
                >
                  <Phone className="text-brand-yellow flex-shrink-0" size={14} />
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </div>

              {/* Branch Office */}
              <div className="pt-3 border-t border-white/10">
                <div className="font-mono text-[10px] tracking-[0.2em] text-brand-yellow uppercase mb-2">
                  Branch Office · Vijayanagar
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=%2319%2C+1766%2C+5th+Main%2C+22nd+Cross+Road%2C+Govindarajanagar%2C+Vijayanagar%2C+Bengaluru+560079"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/70 hover:text-brand-yellow transition-colors group"
                  data-testid="footer-branch-address-link"
                >
                  <MapPin className="text-brand-yellow flex-shrink-0 mt-1" size={16} />
                  <span className="text-sm leading-relaxed group-hover:underline">
                    #19, 1766, 5th Main, 22nd Cross Road, Govindarajanagar, Vijayanagar, Bengaluru – 560079
                  </span>
                </a>
                <a
                  href="tel:+918066065949"
                  className="flex items-center gap-3 mt-2 text-white/70 hover:text-brand-yellow transition-colors"
                >
                  <Phone className="text-brand-yellow flex-shrink-0" size={14} />
                  <span className="text-sm">080-66065949</span>
                </a>
              </div>
            </div>
          </div>

          {/* Site links */}
          <div className="lg:col-span-3">
            <h4 className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase mb-5">
              Navigate
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Services', to: '/services' },
                { label: 'Publications', to: '/publications' },
                { label: 'Firm Profile', to: '/firm-profile' },
                { label: 'Careers', to: '/careers' },
                { label: 'Home', to: '/' },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={() => window.scrollTo(0, 0)}
                    className="group inline-flex items-center gap-2 text-sm text-white/75 hover:text-brand-yellow transition-colors"
                  >
                    <span>{l.label}</span>
                    <ArrowUpRight
                      size={12}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ============= EMBEDDED MAP ============= */}
        <div className="mt-12 pt-12 border-t border-white/10">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
              Visit our office
            </div>
            <a
              href={companyInfo.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-white/60 hover:text-brand-yellow transition-colors inline-flex items-center gap-1"
            >
              Open in Google Maps
              <ArrowUpRight size={12} />
            </a>
          </div>
          <div
            className="relative w-full overflow-hidden border border-white/15"
            style={{ paddingTop: '32%' }}
            data-testid="footer-map-embed"
          >
            <iframe
              title="R S T & Co. Office Location"
              src="https://www.google.com/maps?q=R+S+T+%26+Co.+Chartered+Accountants+%23222+80+Ft+Service+Main+Road+12th+Block+BDA+Layout+Nagarabhavi+Bengaluru+560072&output=embed"
              className="absolute inset-0 w-full h-full"
              style={{ border: 0, filter: 'invert(0) hue-rotate(0deg)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>

        {/* ============= BOTTOM BAR ============= */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-xs text-center md:text-left">
            © {currentYear} {companyInfo.name}. All rights reserved. · Excellence in
            professional services since 2011.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-white/50 hover:text-brand-yellow text-xs transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-white/50 hover:text-brand-yellow text-xs transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
